import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../PartialPayment/PartialPayment.css";
import Loader from "../../../Template/Loader/Loader";
import CommonMessageModal from "../../CommonMessageModal/CommonMessageModal";
import { createCartRazorpayOrder, verifyCartPayment } from "../../../utils/APIs/razorpayPaymentApis";

const CartPartialPayment = ({ checkoutData, onApplyPromoCode, onRemovePromoCode }) => {
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState(checkoutData?.appliedCoupon?.code || "");
  const [loading, setLoading] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);

  const { finalPayable, subtotal, currentDiscount, appliedCoupon, couponApplied, bookingDate, timeSlot, addressData, items, userData, editingStats } = checkoutData;
  const totalAmount = finalPayable;

  const navigateAfterSuccess = () => {
    const hasEditing =
      (items || []).some((item) => item.category?.toLowerCase() === "editing") ||
      (editingStats && editingStats.total > 0);
    if (hasEditing && checkoutData.bookingId) {
      navigate(`/upload-media?bookingId=${checkoutData.bookingId}`, { replace: true });
    } else if (hasEditing && window.lastCreatedBookingId) {
      navigate(`/upload-media?bookingId=${window.lastCreatedBookingId}`, { replace: true });
    } else {
      navigate("/booking-success", { replace: true });
    }
  };

  const SPLIT_THRESHOLD = 1000000;
  const SAFE_MAX_AMOUNT = 450000;

  const calculatePayableAmount = (amount) => {
    if (amount > SPLIT_THRESHOLD) {
      let slicesNeeded = Math.ceil(amount / SAFE_MAX_AMOUNT);
      let dynamicStepAmount = Math.ceil(amount / slicesNeeded);
      return Math.min(amount, dynamicStepAmount, SAFE_MAX_AMOUNT);
    }
    return Math.ceil(amount / 2);
  };

  const payableNow = calculatePayableAmount(totalAmount);
  const outstandingAmount = totalAmount - payableNow;

  const handlePartialPayment = async () => {
    try {
      setLoading(true);

      const res = await createCartRazorpayOrder({
        amount: payableNow,
        bookingDetails: {
          date: bookingDate,
          time: timeSlot,
          specialInstructions: ""
        }
      });

      if (res?.data?.success) {
        const order = res.data.order;
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_YourKeyID",
          amount: order.amount,
          currency: order.currency,
          name: "Veroa Photography",
          description: "Cart Checkout - Partial Payment",
          order_id: order.id,
          handler: async function (response) {
            try {
              setLoading(true);
              const verifyRes = await verifyCartPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                couponId: appliedCoupon?._id || null,
                bookingDetails: {
                  date: bookingDate,
                  time: timeSlot,
                  address: addressData.location,
                  city: addressData.city,
                  state: addressData.state,
                  postalCode: addressData.postalCode,
                  flatOrHouseNo: addressData.flatOrHouseNo,
                  streetName: addressData.streetName,
                  lat: addressData.lat,
                  lng: addressData.lng,
                  specialInstructions: "",
                  totalAmount: subtotal,
                  finalAmount: finalPayable,
                  discountAmount: currentDiscount,
                  couponApplied: couponApplied,
                  couponId: appliedCoupon?._id || null,
                  eventType: checkoutData.cartData?.eventType || checkoutData.eventType || "",
                }
              });

              if (verifyRes?.data?.success) {
                const createdBookings = verifyRes.data.bookings || [];
                const firstBooking = createdBookings[0];
                if (firstBooking?._id) {
                  window.lastCreatedBookingId = firstBooking._id;
                }
                setIsPaymentSuccess(true);
                navigateAfterSuccess();
              } else {
                setIsPaymentSuccess(false);
                setModalMessage("Payment verification failed.");
                setShowModal(true);
              }
            } catch (err) {
              setIsPaymentSuccess(false);
              setModalMessage("Payment verification error.");
              setShowModal(true);
            } finally {
              setLoading(false);
            }
          },
          prefill: {
            name: userData?.username,
            email: userData?.email,
            contact: userData?.mobileNumber
          },
          theme: { color: "#4A3326" }
        };
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
          setIsPaymentSuccess(false);
          setModalMessage(response.error.description || "Payment failed.");
          setShowModal(true);
        });
        rzp.open();
      }
    } catch (error) {
      setIsPaymentSuccess(false);
      setModalMessage("Failed to initiate checkout.");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="partial-payment-container">
        <div className="partial-payment-content">
          <div className="partial-details-text">
            <p className="pay-now-text">Pay <span className="highlight-green">₹{payableNow}</span> now</p>
            <p className="outstanding-text">Outstanding amount <span className="highlight-yellow">₹{outstandingAmount}</span> to be paid after the event</p>
          </div>
          <div className="promo-code-section">
            <h3 className="promo-title">Have a promo code?</h3>
            <div className="promo-input-group">
              <input 
                type="text" 
                placeholder="Enter Code" 
                value={promoCode} 
                onChange={(e) => setPromoCode(e.target.value)} 
                className="promo-input" 
                disabled={couponApplied}
              />
              <button 
                className="btn-apply" 
                disabled={couponLoading}
                onClick={() => couponApplied ? onRemovePromoCode() : onApplyPromoCode(promoCode, setCouponLoading)}
              >
                {couponLoading ? "Applying..." : (couponApplied ? "Remove" : "Apply")}
              </button>
            </div>
          </div>
          <div className="payment-summary-grid">
            <div className="summary-item">
              <span className="summary-amount green-text">₹{totalAmount}</span>
              <span className="summary-label">Total Payable<br />Amount</span>
            </div>
            <div className="summary-item">
              <span className="summary-amount silver-text">₹{payableNow}</span>
              <span className="summary-label">Upfront<br />Payment<br />(Now)</span>
            </div>
            <div className="summary-item">
              <span className="summary-amount gold-text">₹{outstandingAmount}</span>
              <span className="summary-label">Outstanding<br />Amount</span>
            </div>
          </div>
          <button className="btn-continue-payment" onClick={handlePartialPayment} disabled={loading}>
            Continue to Payment
          </button>
        </div>
      </div>
      <CommonMessageModal
        show={showModal}
        message={modalMessage}
        onClose={() => {
          setShowModal(false);
          if (isPaymentSuccess) {
            const hasEditing = (items || []).some(item => item.category?.toLowerCase() === 'editing') || (editingStats && editingStats.total > 0);
            if (hasEditing && checkoutData.bookingId) {
                navigate(`/upload-media?bookingId=${checkoutData.bookingId}`, { replace: true });
            } else if (hasEditing && window.lastCreatedBookingId) {
                 navigate(`/upload-media?bookingId=${window.lastCreatedBookingId}`, { replace: true });
            } else {
                navigate("/booking-success", { replace: true });
            }
          }
        }}
      />
    </>
  );
};

export default CartPartialPayment;
